import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import en from './locales/en.json'

/**
 * English is the only hand-edited locale. Every other language is generated
 * from it by scripts/translate-locales.mjs, and loaded on demand so a visitor
 * only ever downloads their own language.
 */
export const LANGUAGES = {
  en: 'English',
  es: 'Español',
  pt: 'Português',
  fr: 'Français',
  de: 'Deutsch',
  vi: 'Tiếng Việt',
  tl: 'Tagalog',
  zh: '中文',
} as const

export type LanguageCode = keyof typeof LANGUAGES

const loaders: Record<Exclude<LanguageCode, 'en'>, () => Promise<{ default: unknown }>> = {
  es: () => import('./locales/es.json'),
  pt: () => import('./locales/pt.json'),
  fr: () => import('./locales/fr.json'),
  de: () => import('./locales/de.json'),
  vi: () => import('./locales/vi.json'),
  tl: () => import('./locales/tl.json'),
  zh: () => import('./locales/zh.json'),
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: { en: { translation: en } },
    fallbackLng: 'en',
    supportedLngs: Object.keys(LANGUAGES),
    // Treat es-MX, pt-BR etc. as their base language rather than falling to English.
    load: 'languageOnly',
    interpolation: { escapeValue: false },
    detection: { order: ['querystring', 'navigator', 'htmlTag'], lookupQuerystring: 'lang' },
  })

/** Loads a language's strings the first time it is selected. */
export const loadLanguage = async (code: LanguageCode) => {
  if (code !== 'en' && !i18n.hasResourceBundle(code, 'translation')) {
    try {
      const mod = await loaders[code]()
      i18n.addResourceBundle(code, 'translation', mod.default, true, true)
    } catch {
      // A missing locale file falls back to English rather than breaking the page.
      return
    }
  }
  await i18n.changeLanguage(code)
  document.documentElement.lang = code
}

// The detector may have chosen a language before its bundle existed.
const detected = i18n.language?.split('-')[0] as LanguageCode | undefined
if (detected && detected !== 'en' && detected in LANGUAGES) void loadLanguage(detected)
document.documentElement.lang = detected && detected in LANGUAGES ? detected : 'en'

export { i18n }
